import { keyBy } from 'lodash';
import i18next from 'i18next';
import React from 'react';

import { Dropdown } from '@commons/utils/styledLibraryComponents';
import {
  GENERIC_MODAL_ADD_BUTTON,
  GENERIC_MODAL_BACK_BUTTON,
  GENERIC_MODAL_CANCEL_BUTTON,
  GENERIC_MODAL_CONFIRM_BUTTON,
  GENERIC_MODAL_NEXT_BUTTON,
  GENERIC_MODAL_SAVE_BUTTON,
} from '@commons/Modals/GenericModal/genericModalActions';
import { getConfirmationModal } from '@commons/Modals/ConfirmationModal';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { ModalContainer, ModalDropdownItem } from '../styledComponents';

import AssociateSupplierProductsModal from '@admin/inventories/utils/associateSupplierProductsModal';
import RecipesMappingsModal from '@admin/inventories/utils/recipeMappings.utils';

const InventoryListTemplateToCopySelectionModalContent = (props) => {
  const {
    availableInventoryListTemplates,
    inventoryListTemplateToCopy,
    setInventoryListTemplateToCopy,
    stores,
  } = props;

  const storesKeyById = keyBy(stores, 'id');

  const formattedAvailableInventoryListTemplates = availableInventoryListTemplates.map(
    (inventoryListTemplate) => ({
      ...inventoryListTemplate,
      renderValue: () => (
        <ModalDropdownItem>
          <Text color={ENUM_COLORS.BLACK} font={ENUM_FONTS.TEXT_MIDDLE_NORMAL}>
            {inventoryListTemplate.name}
          </Text>
          <Text color={ENUM_COLORS.DARK} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
            {storesKeyById[inventoryListTemplate.storeId]?.name}
          </Text>
        </ModalDropdownItem>
      ),
    }),
  );

  return (
    <ModalContainer>
      <Dropdown
        customStyle={{ position: 'inherit' }}
        height={'64px'}
        items={formattedAvailableInventoryListTemplates}
        label={i18next.t('GENERAL.INVENTORY_LIST_TEMPLATE_SINGLE')}
        selectedItem={inventoryListTemplateToCopy}
        isRequired
        isUniqueSelection
        onSelectionChange={setInventoryListTemplateToCopy}
      />
    </ModalContainer>
  );
};

export const getCopyParamsConfirmationModalConfig = ({
  handleParamsCopy,
  inventoryListTemplateToCopy,
  selectedILTsCount,
}) => ({
  ...getConfirmationModal({
    title: i18next.t(
      'ADMIN.INVENTORY_LIST_TEMPLATES.COPY_INVENTORY_LIST_TEMPLATE_PARAMS_WARNING_TITLE',
    ),
    content: i18next.t(
      'ADMIN.INVENTORY_LIST_TEMPLATES.COPY_INVENTORY_LIST_TEMPLATE_PARAMS_WARNING',
      {
        count: selectedILTsCount,
        sourceTemplateName: inventoryListTemplateToCopy.name,
      },
    ),
    icon: '/images/inpulse/warning-white-small.svg',
    actions: [
      GENERIC_MODAL_CANCEL_BUTTON(),
      {
        ...GENERIC_MODAL_CONFIRM_BUTTON(),
        handleClick: () => handleParamsCopy(),
      },
    ],
  }),
  width: '542px',
});

export const getCopyParamModalConfig = ({
  handleParamsCopyConfirmation,
  availableInventoryListTemplates,
  inventoryListTemplateToCopy,
  setInventoryListTemplateToCopy,
  stores,
  resetCopyModal,
}) => ({
  type: 'action',
  width: '542px',
  height: 'auto',
  icon: '/images/inpulse/gear-ipblack-small.svg',
  title: i18next.t(
    'ADMIN.INVENTORY_LIST_TEMPLATES.COPY_INVENTORY_LIST_TEMPLATE_PARAMS_MODAL_TITLE',
  ),
  handleCloseCleanUp: resetCopyModal,
  component: InventoryListTemplateToCopySelectionModalContent,
  data: {
    availableInventoryListTemplates,
    inventoryListTemplateToCopy,
    setInventoryListTemplateToCopy,
    stores,
  },
  actions: [
    GENERIC_MODAL_CANCEL_BUTTON(),
    {
      ...GENERIC_MODAL_SAVE_BUTTON(),
      isDisabled: !inventoryListTemplateToCopy,
      preventClosing: true,
      handleClick: () => handleParamsCopyConfirmation(),
    },
  ],
});

export const ADD_REFERENCE_TO_INVENTORY_LIST_TEMPLATE_MODAL_STEPS = {
  ADD_SUPPLIER_PRODUCTS: 'addSupplierProducts',
  ADD_RECIPES: 'addRecipes',
};

const getAddReferenceStepModalActions = (
  currentStep,
  setCurrentStep,
  handleAddingReferences,
  resetModal,
) => {
  if (currentStep === ADD_REFERENCE_TO_INVENTORY_LIST_TEMPLATE_MODAL_STEPS.ADD_SUPPLIER_PRODUCTS) {
    return [
      GENERIC_MODAL_CANCEL_BUTTON(),
      {
        ...GENERIC_MODAL_NEXT_BUTTON(),
        preventClosing: true,
        handleClick: () => {
          setCurrentStep(ADD_REFERENCE_TO_INVENTORY_LIST_TEMPLATE_MODAL_STEPS.ADD_RECIPES);
        },
      },
    ];
  }

  return [
    {
      ...GENERIC_MODAL_BACK_BUTTON(),
      preventClosing: true,
      handleClick: () => {
        setCurrentStep(ADD_REFERENCE_TO_INVENTORY_LIST_TEMPLATE_MODAL_STEPS.ADD_SUPPLIER_PRODUCTS);
      },
    },
    {
      ...GENERIC_MODAL_ADD_BUTTON(),
      handleClick: () => {
        handleAddingReferences();
        resetModal();
        setCurrentStep(ADD_REFERENCE_TO_INVENTORY_LIST_TEMPLATE_MODAL_STEPS.ADD_SUPPLIER_PRODUCTS);
      },
    },
  ];
};

export const getAddReferenceStepModalConfig = ({
  currentStep,
  setCurrentStep,
  hasLocalCatalogs,
  setSpToBeAdded,
  availableSPForAssociation,
  alreadySelectedSupplierProducts,
  clientId,
  setRecipesToBeAdded,
  storeIds,
  alreadyMappedRecipes,
  handleAddingReferences,
  resetModal,
}) => ({
  type: 'actionWhite',
  isFullscreen: true,
  icon: '/images/inpulse/add-black-small.svg',
  title:
    currentStep === ADD_REFERENCE_TO_INVENTORY_LIST_TEMPLATE_MODAL_STEPS.ADD_SUPPLIER_PRODUCTS
      ? i18next.t('ADMIN.INVENTORY_LIST_TEMPLATES.ADD_SUPPLIER_PRODUCTS_STEP_MODAL_TITLE')
      : i18next.t('ADMIN.INVENTORY_LIST_TEMPLATES.ADD_RECIPE_STEP_MODAL_TITLE'),
  handleCloseCleanUp: resetModal,
  actions: getAddReferenceStepModalActions(
    currentStep,
    setCurrentStep,
    handleAddingReferences,
    resetModal,
  ),
  component:
    currentStep === ADD_REFERENCE_TO_INVENTORY_LIST_TEMPLATE_MODAL_STEPS.ADD_SUPPLIER_PRODUCTS
      ? AssociateSupplierProductsModal
      : RecipesMappingsModal,
  data: {
    hasLocalCatalogs,
    setSpToBeAdded,
    availableSPForAssociation,
    alreadySelectedSupplierProducts,
    clientId,
    setRecipesToBeAdded,
    storeIds,
    alreadyMappedRecipes,
  },
  componentOverflow: 'hidden',
  step: {
    total: 2,
    current:
      currentStep === ADD_REFERENCE_TO_INVENTORY_LIST_TEMPLATE_MODAL_STEPS.ADD_SUPPLIER_PRODUCTS
        ? 1
        : 2,
  },
  customPadding: '24px 0 0 0',
});

export default {
  getCopyParamModalConfig,
  getCopyParamsConfirmationModalConfig,
  getAddReferenceStepModalConfig,
};
